export interface FAQItem {
  question: string;
  answer: string;
}

export interface FAQSection {
  title: string;
  items: FAQItem[];
}

export const FAQ_SECTIONS: FAQSection[] = [
  {
    title: 'Platform Basics',
    items: [
      {
        question: 'What is TradeMagic?',
        answer: `<p>TradeMagic is a peer-to-peer Magic: The Gathering card trading platform that uses asynchronous point-based trading. Unlike traditional trading where you need to find exact trade matches, TradeMagic lets you send cards to earn points, then spend those points on cards you want—no haggling or negotiation needed.</p>`,
      },
      {
        question: 'How does point-based trading work?',
        answer: `<p>Points work like trade credits. When you send cards to other traders, you earn points (100 points = approximately $1 in trade value). Use those points to receive cards from other traders' inventories. The system handles pricing automatically based on market data, eliminating haggling and negotiation.</p>`,
      },
      {
        question: 'How do I get started?',
        answer: `<p><strong>Getting started takes about 15 minutes:</strong></p>
<p>1. Sign up and verify your email and phone number<br/>
2. Add 20-30 cards to your Want List (the more you add, the faster you'll receive cards)<br/>
3. Browse the Send Cards page to find trade opportunities<br/>
4. Ship your first trade within 72 hours using our shipping guide</p>`,
      },
      {
        question: 'Is TradeMagic free to use?',
        answer: `<p>Yes, the beta is completely free. There's a small transaction point-fee (1-5% depending on economic health) that destroys points to keep the economy stable, and a monthly maintenance fee of 100 Trade Credits (~$1) with a 50% discount for active traders. Core trading functionality will always remain accessible.</p>`,
      },
    ],
  },
  {
    title: 'How TradeMagic Compares',
    items: [
      {
        question: 'How does TradeMagic differ from Cardsphere?',
        answer: `<p>Cardsphere uses a discount system where you often need to offer 10-20% above face value (bounties) to get cards sent to you, which creates complicated economics and waiting. TradeMagic uses pure market-based pricing where 100 points = $1, with no discounts or bounties needed. We have simpler economics and transparent point supply controls built from PucaTrade's lessons.</p>`,
      },
      {
        question: 'How does TradeMagic differ from Deckbox?',
        answer: `<p>Deckbox requires exact trade matches—you need to find someone who has what you want AND wants what you have, then negotiate the details. TradeMagic's asynchronous trading means you can send cards to anyone and receive cards from anyone without needing exact matches. Plus we have a modern mobile-optimized interface and no back-and-forth negotiation.</p>`,
      },
      {
        question: "What's the difference between TradeMagic and Discord/Reddit trading?",
        answer: `<p>TradeMagic eliminates the "who ships first" negotiation, spreadsheet management, and trust issues of Discord/Reddit trading. Trades happen at market value with no haggling, there's built-in security with trade case resolution, and you don't need to coordinate timing with your trading partner. You get efficient trading without the friction and security concerns.</p>`,
      },
      {
        question: 'Is TradeMagic better than selling to buylists (Card Kingdom, etc.)?',
        answer: `<p>It depends on your goal. Buylists typically pay 50-70% of retail value but give you cash immediately. TradeMagic gives you 99% value (minus 1% transaction fee) but in trade credits, not cash. If you need cash immediately, buylists make sense. If you want to rotate your collection into different cards while maintaining full value, TradeMagic is significantly better.</p>`,
      },
      {
        question: 'How is TradeMagic different from TCGPlayer Direct?',
        answer: `<p>TCGPlayer Direct charges 13% in fees plus shipping costs, and you're selling at market price then buying at market price (losing value on both ends). TradeMagic charges 1-5% transaction fees and you maintain full trade value—you're not selling low and buying high. TCGPlayer is faster if you need cash, but TradeMagic is more efficient if you want different cards.</p>`,
      },
    ],
  },
  {
    title: 'Economics & Security',
    items: [
      {
        question: 'What prevents the platform from creating unlimited points?',
        answer: `<p>We maintain a public economic dashboard (updated daily) showing total points in circulation, active traders, and all key metrics at <a href="https://trademagic.gg/articles/sustainable-economics">trademagic.gg/articles/sustainable-economics</a>. If we ever printed points without corresponding card trades, the supply spike would be immediately visible to everyone. Transparency is our accountability.</p>`,
      },
      {
        question: 'How do points enter and exit the system?',
        answer: `<p><strong>Points Enter:</strong> Only when new users sign up and complete mobile verification (1000 bonus points). Trades just move points between users—they don't create new points. We never create promotional points.</p>
<p><strong>Points Exit:</strong> Through transaction fees (1-5%), monthly maintenance fees (100 TC with 50% discount for active traders), and inactive account cleanup. Multiple exit mechanisms prevent the inflation that killed PucaTrade.</p>`,
      },
      {
        question: 'What stops stores or power users from accumulating thousands of points?',
        answer: `<p>There's no hard cap on points, but this isn't actually a problem. High-volume trading constantly destroys points through transaction fees (1-5% per trade). If a store sends thousands of dollars of cards, they're providing real value to players who want those cards, and the fee structure ensures they're contributing to economic stability. More inventory in the system means better liquidity for everyone.</p>`,
      },
      {
        question: 'How do you handle fraud and scammers?',
        answer: `<p><strong>Prevention:</strong> Mobile verification required (prevents multiple accounts), tracking required for trades over $50, trade case system for disputes.</p>
<p><strong>When problems occur:</strong> Admin review with documented resolution. Clear fraud results in permanent ban and point forfeiture. The advantage of a point system is we can refund both parties in ambiguous cases without losing money, which lets us be generous with benefit-of-the-doubt situations.</p>`,
      },
      {
        question: 'How do you handle condition disputes?',
        answer: `<p>Our admin team reviews condition disputes and mediates. In clear cases where a card is obviously not as described, the at-fault party loses and the innocent party gets refunded. In ambiguous cases where fault can't be determined, we refund points to both parties, document the incident in their files, and monitor for patterns. Photo evidence for disputes is coming soon to make these calls even clearer.</p>`,
      },
      {
        question: 'What happens if TradeMagic shuts down?',
        answer: `<p>Honest answer: All points would become worthless, just like any platform currency. Our mitigation strategy is radical transparency—you can see the economic health in real-time on our public dashboard. We've built the platform with economic sustainability as the foundation (learning from PucaTrade's mistakes), but you should only send cards you're comfortable with.</p>`,
      },
    ],
  },
  {
    title: 'Trading Mechanics',
    items: [
      {
        question: 'Can I set minimum order values or trade requirements?',
        answer: `<p>You don't need to set stipulations. As a sender, you have complete control over which trades you initiate. The Send Cards page shows all cards that people want AND can afford. You browse these opportunities and only initiate trades that meet your criteria (minimum value, specific cards, etc.). Receivers can't "request" cards from you—you choose when to send.</p>`,
      },
      {
        question: 'How are card prices determined?',
        answer: `<p>Currently, pricing is based on TCGPlayer market prices, updated regularly. You can't set custom prices—the goal is to keep things simple and ensure everyone gets fair trade value. In the future we may incorporate dynamic pricing based on actual TradeMagic supply and demand data. If you disagree with a card's price, simply don't list it.</p>`,
      },
      {
        question: 'Why do some cards show $0 value?',
        answer: `<p>For cards with very low sales data (unusual versions, specific foils from older sets), we may not have pricing yet. This affects a small percentage of cards. We're actively working to complete the catalog, and as more trades happen on the platform, we'll have better data for these edge cases.</p>
<p>If you encounter a card with missing pricing, please click the "Feedback" button on any page to report it, and we'll update it manually.</p>`,
      },
      {
        question: 'Can I trade internationally?',
        answer: `<p>Yes! International trading works the same as domestic. Use Global Forever Stamps ($1.65) instead of regular Forever Stamps. Domestic-only filtering is coming soon for users who prefer to trade within their own country.</p>`,
      },
      {
        question: 'What about high-value cards like Reserved List cards?',
        answer: `<p>Reserved List and high-value cards are fully supported. Transaction fees scale with value (1-5%), but this is still far better than buylist spreads (50-70% of value) or TCGPlayer fees (13%). Always use tracking for trades over $50, and follow our shipping guide for proper packaging of expensive cards.</p>`,
      },
    ],
  },
  {
    title: 'Getting Started',
    items: [
      {
        question: "I'm not receiving verification emails (Yahoo Mail users)",
        answer: `<p><strong>Known issue with Yahoo Mail:</strong> Yahoo's servers aggressively throttle emails from shared IP addresses. Your email was sent successfully but may be delayed 1-2 hours. Check your spam/junk folder. If nothing arrives, contact support for manual verification. <strong>Workaround:</strong> Register with Gmail, Outlook, or another provider instead.</p>`,
      },
      {
        question: 'Can I import my existing collection?',
        answer: `<p>Yes! We support CSV import from Deckbox, Cardsphere, and other platforms. This eliminates the need to manually re-enter thousands of cards. Just export your collection from your current platform and import it directly to TradeMagic.</p>`,
      },
      {
        question: "What's the learning curve?",
        answer: `<p>Most users complete their first trade within 24-48 hours of signing up. The easy parts: browsing cards, adding to Want List, sending cards. The slightly confusing parts: version selection (we're improving this), understanding point economics. Setup takes about 15 minutes total.</p>`,
      },
      {
        question: 'How do I get support if something goes wrong?',
        answer: `<p>During beta, we're very responsive. Contact us via Discord (fastest response, usually same-day), email at support@trademagic.gg, or use the feedback button in the site. Response times are currently hours, not days, since we're small and focused on user experience.</p>`,
      },
    ],
  },
  {
    title: 'Platform History & Trust',
    items: [
      {
        question: 'What happened to PucaTrade and why is TradeMagic different?',
        answer: `<p>PucaTrade closed because points entered the system faster than they left (promotions, bounties, giveaways), causing massive inflation. Cards that cost 5,000 points at TCG price were requiring 8,000-10,000 points to actually receive. People stopped sending cards and the platform died.</p>
<p><strong>What we're doing differently:</strong> Multiple point sinks from day one (transaction fees, monthly fees, inactive cleanup), no promotional point creation, public economic dashboard with daily updates, and dynamic fees that adjust automatically to economic conditions.</p>`,
      },
      {
        question: 'Who is behind TradeMagic?',
        answer: `<p>TradeMagic is built by Eric, who was on the founding team of PucaTrade and has 15+ years building products in gaming and streaming. After PucaTrade closed, he spent years figuring out how to build a sustainable point-based trading platform that doesn't repeat those mistakes.</p>`,
      },
      {
        question: 'How will TradeMagic make money?',
        answer: `<p>Eventually through premium subscription tiers (think Discord Nitro) for advanced features like portfolio analytics, price alerts, and API access. Transaction fees destroy points—they don't generate revenue. They exist purely for economic stability. Core trading functionality will always remain accessible.</p>`,
      },
    ],
  },
];

// Helper function to generate FAQPage schema for SEO
export const generateFAQSchema = () => {
  const mainEntity = FAQ_SECTIONS.flatMap((section) =>
    section.items.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        // Strip HTML tags for schema
        text: item.answer.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim(),
      },
    }))
  );

  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity,
  };
};
